import React, { useMemo } from 'react';
import { Modal, Typography } from 'antd';
import { type AccessoryData } from '../../../../types/process/order-data';

const { Text } = Typography;

interface AccessoryModalProps {
  visible: boolean;
  onClose: () => void;
  accessoryData?: AccessoryData[];
}

export const AccessoryModal: React.FC<AccessoryModalProps> = ({ visible, onClose, accessoryData }) => {
  const accessoryGroups = useMemo(() => {
    const list: AccessoryData[] = Array.isArray(accessoryData) ? (accessoryData as AccessoryData[]) : [];
    const groupMap = new Map<string, AccessoryData[]>();
    list.forEach((item) => {
      const raw = String(item?.accessory_type ?? '').trim();
      const parsed = Number.parseInt(raw, 10);
      const key = Number.isFinite(parsed) ? String(parsed) : raw || '配件';
      const arr = groupMap.get(key) || [];
      arr.push(item);
      groupMap.set(key, arr);
    });

    const styleByType = (t: number | null) => {
      if (t === 1) return { background: '#E6FFFB', border: '#87E8DE', labelColor: '#08979C' };
      if (t === 2) return { background: '#FFF1F0', border: '#FFA39E', labelColor: '#CF1322' };
      if (t === 3) return { background: '#FFF7E6', border: '#FFD591', labelColor: '#D46B08' };
      return { background: '#F5F5F5', border: '#D9D9D9', labelColor: 'rgba(0,0,0,0.65)' };
    };

    const daysFromItems = (items: AccessoryData[]) => {
      for (const it of items) {
        const name = String(it?.accessory_name ?? '');
        const m = name.match(/(\d+)\s*天/);
        if (m?.[1]) return Number(m[1]);
      }
      return undefined;
    };

    const labelByType = (t: number | null, raw: string, items: AccessoryData[]) => {
      if (t === 1) return '赠品';
      if (t === 2) return '需归还';
      if (t === 3) {
        const days = daysFromItems(items);
        return `租满${days ?? 'x'}天无需归还`;
      }
      return raw || '配件';
    };

    return Array.from(groupMap.entries()).map(([rawKey, items]) => {
      const parsed = Number.parseInt(rawKey, 10);
      const t = Number.isFinite(parsed) ? parsed : null;
      return { key: rawKey, label: labelByType(t, rawKey, items), items, style: styleByType(t) };
    });
  }, [accessoryData]);

  return (
    <Modal
      title="配件清单"
      open={visible}
      onCancel={onClose}
      footer={null}
    >
      {(accessoryData?.length || 0) === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>暂无配件信息</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {accessoryGroups.map((g) => {
            return (
              <div
                key={g.key}
                style={{
                  background: '#fff',
                  border: `1px solid ${g.style.border}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div style={{ background: g.style.background, padding: '10px 12px', fontWeight: 600, color: g.style.labelColor }}>
                  {g.label}
                </div>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {g.items.map((it, idx) => (
                    <div key={`${it.accessory_name}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                       <div style={{ display: 'flex', gap: 8 }}>
                         <Text>{it.accessory_name}</Text>
                         {it.quantity ? <Text type="secondary">x{it.quantity}</Text> : null}
                       </div>
                       <Text strong>¥{it.accessory_price}</Text>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
